"use client";

import { cn } from "@/lib/utils";
import Link from "next/link";
import { usePathname } from "next/navigation";

interface CategoriesProps {
  categories: string[]; // ex: ['Markets', 'Finance', 'Sustainability', ...]
  activeCategory: string; // slug: 'markets', 'finance', ...
  className?: string;
  elementId?: string;
}

function slugifyCategory(category: string) {
  return category.toLowerCase().replace(/\s+/g, "-");
}

export default function Categories({
  categories,
  className,
  elementId,
}: CategoriesProps) {
  const pathname = usePathname();

  const routeCategory = pathname.split("/")[1];

  const activeSlugCategory = routeCategory || "markets";

  return (
    <div className={cn("relative lg:w-full w-full h-full", className)}>
      <ul className="w-full h-full flex flex-col lg:flex-row items-center justify-center lg:overflow-x-auto overflow-x-auto relative max-w-[172.5rem] mx-auto lg:px-0 px-[2rem]">
        {categories?.map((categoryLabel, index) => {
          const slug = slugifyCategory(categoryLabel);
          const isActive = slug === activeSlugCategory;

          return (
            <li
              key={index}
              data-slug={slug}
              className="relative w-full lg:w-fit h-full flex flex-col items-center justify-center group"
            >
              <CategoryItem elementId={elementId} slug={slug}>
                <span
                  className={cn(
                    "text-[2.5rem] lg:text-[1.75rem] transition-colors duration-300 font-medium tracking-[-0.06rem] uppercase text-black/80 lg:text-black/70 group-hover:text-black lg:px-[3rem] py-[2.5rem] lg:py-0",
                    isActive && "text-black"
                  )}
                >
                  {categoryLabel}
                </span>

                {isActive ? (
                  <div className="absolute bottom-0 w-full h-[0.25rem] lg:h-[0.5rem] bg-[#F4411C]" />
                ) : (
                  <div className="lg:hidden absolute bottom-0 w-full h-[0.25rem] bg-black/5" />
                )}
              </CategoryItem>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function CategoryItem({
  slug,
  children,
  elementId,
}: {
  slug: string;
  children: React.ReactNode;
  elementId?: string;
}) {
  const href = elementId ? `/${slug}#${elementId}` : `/${slug}`;

  return (
    <Link
      href={href}
      prefetch
      className="w-full h-full flex items-center lg:justify-center cursor-pointer relative"
    >
      {children}
    </Link>
  );
}
